import React, {useState, useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {StyledMessagePlatformList} from "./MessagePlatform.styled";
import {hideLoader, showLoader} from "../../store/common/slice";
import {useAccessToken} from "../../common/hooks/useAccessToken";
import Button from "../../common/Button";
import {NotificationManager} from "react-notifications";
import {isAdminSelector} from "../../store/auth/selectors";
import sms from "../../images/newDesign/sms.svg";
import email from "../../images/newDesign/email.svg";
import arrowDownBlack from "../../images/newDesign/arrow_down_black.svg";
import {ChecklistDropdown} from "../../common/checklist_dropdown/ChecklistDropdown";

const messageMethods = {
    sms: 'sms',
    email: 'email'
}

const contacts = {
    "chooseTenants": "chooseTenants",
    "owners": "owners",
    "renters": "renters"
}

const MAX_SENDER_NAME_LENGTH = 11;

export function MessagePlatformList({buildingID}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const textareaRef = useRef(null);

    const client = useAccessToken();

    const managementCompany = useSelector(state => state.buildings.currentBuildingData.managementCompany);
    const isAdmin = useSelector(isAdminSelector);
    const ltr = useSelector((state) => state.common.ltrEnabled);

    const [currentMethod, setCurrentMethod] = useState(messageMethods.sms);
    const [currentContactSwitch, setCurrentContactSwitch] = useState(contacts.chooseTenants);
    const [apartments, setApartments] = useState([]);
    const [apartmentDropdownOptions, setApartmentDropdownOptions] = useState([]);
    const [allApartmentRenters, setAllApartmentRenters] = useState([]);
    const [allApartmentOwners, setAllApartmentOwners] = useState([]);
    const [selectedApts, setSelectedApts] = useState([]);
    const [personalizedMessage, setPersonalizedMessage] = useState('');
    const [senderName, setSenderName] = useState('');
    const [showTenantList, setShowTenantList] = useState(false);
    const [showCustomizedContentPopup, setShowCustomizedContentPopup] = useState(false);
    const [messagesCount, setMessagesCount] = useState({
        email: 0,
        sms: 0
    });

    useEffect(async () => {
        if (!client) return;
        await loadApartments();
    }, [client]);

    useEffect(async () => {
        let allOptions = [];
        let ownersOptions = [];
        let rentersOptions = [];
        apartments?.forEach((apt) => {
            apt.tenants.forEach((tenant) => {
                const option = {
                    //todo hide tenants without phone/email depends on search
                    value: {
                        apartmentNumber: apt.apartmentNum,
                        tenantID: tenant.tenant.id,
                        type: tenant.tenant.tenantType
                    },
                    label: ` ${t('messagePlatform.labels.apartment')} ${apt.apartmentNum} - ${tenant.details.name}`
                }
                if (tenant.tenant.isPrimary) {
                    allOptions.push(option);
                    tenant.tenant.tenantType === 'owner' && ownersOptions.push(option);
                    tenant.tenant.tenantType === 'renter' && rentersOptions.push(option);
                }
            })
        })
        setApartmentDropdownOptions(allOptions);
        setAllApartmentOwners(ownersOptions);
        setAllApartmentRenters(rentersOptions);
    }, [apartments]);

    useEffect(() => {
        if (managementCompany?.externalId) {
            const shortName = managementCompany.externalId.substring(0,MAX_SENDER_NAME_LENGTH);
            const shortNameWithoutSpecialChars = shortName.replace(/[^a-zA-Z0-9@.]/g, "");
            setSenderName(shortNameWithoutSpecialChars);
        }
    }, [managementCompany]);

    const loadApartments = async () => {
        dispatch(showLoader());
        const apartmentResponse = await client.tenants.getTenantsForBuilding(buildingID);
        setApartments(apartmentResponse.apartments.sort((a, b) => a.apartmentNum - b.apartmentNum));
        const messagesCountResponse = await client.managers.operations.getMessagesCount(buildingID);
        setMessagesCount(messagesCountResponse.messageCount)
        dispatch(hideLoader());
    };

    async function handleSendMessageClick() {
        dispatch(showLoader());
        const params = {
            "tenants": [],
            "senderName": senderName,
            "methods": {
                "bySMS": currentMethod === messageMethods.sms,
                "byEmail": currentMethod === messageMethods.email
            },
            "personalizedMessage": currentMethod === messageMethods.email ? convertNewLineToEmailStyle() : personalizedMessage
        }
        const apts = getSelectedContactApartments()
        for (let i = 0; i < apts.length; i++) {
            let value = apts[i].value;
            params.tenants.push({
                "type": value.type,
                "apartmentNumber": value.apartmentNumber,
                "tenantID": value.tenantID
            })
        }
        const response = await client.managers.operations.sendMultiplePlatformMessages(buildingID, params);
        if (response.success){
            NotificationManager.success(t('messagePlatform.messages.successSending', {numberOfTenants: params.tenants.length}))
        }
        setSelectedApts([]);
        setPersonalizedMessage('');
        dispatch(hideLoader());
    }


    function onUpdateMessageInputValues(e) {
        setPersonalizedMessage(e.target.value);
    }

    function onUpdateSenderInputValues(e) {
        const stringWithoutSpecialChars = e.target.value.replace(/[^a-zA-Z0-9@.]/g, "");
        if (stringWithoutSpecialChars.length <= MAX_SENDER_NAME_LENGTH) {
            setSenderName(stringWithoutSpecialChars);
        }
    }

    function handleMessageCharactersPopup() {
        setShowCustomizedContentPopup(!showCustomizedContentPopup);
    }
    function handleTenantList() {
        setShowTenantList(!showTenantList);
    }

    function addCharacter(text) {
        const index = textareaRef.current.selectionStart;
        const newContent = [personalizedMessage.slice(0, index), ` {{${text}}}`, personalizedMessage.slice(index)].join('');
        setPersonalizedMessage(newContent);
        textareaRef.current.focus();
    }

    function convertNewLineToEmailStyle(){
        return personalizedMessage.replaceAll('\n', '<br>');
    }

    function getSelectedContactApartments() {
        if (currentContactSwitch === contacts.chooseTenants) {
            return selectedApts;
        } else if (currentContactSwitch === contacts.renters) {
            return allApartmentRenters;
        } else if (currentContactSwitch === contacts.owners) {
            return allApartmentOwners;
        }
    }
    function getCustomizedButtons() {
        return (
            <div className={"customized-content-pop-up"}>
                <Button onClick={() => {addCharacter(t('messagePlatform.btns.buildingAddress'))}} className="customized-content">{t('messagePlatform.btns.buildingAddress')}</Button>
                <Button onClick={() => {addCharacter(t('messagePlatform.btns.privateName'))}} className="customized-content">{t('messagePlatform.btns.privateName')}</Button>
                <Button onClick={() => {addCharacter(t('messagePlatform.btns.paymentLink'))}} className="customized-content">{t('messagePlatform.btns.paymentLink')}</Button>
                <Button onClick={() => {addCharacter(t('messagePlatform.btns.apartmentNumber'))}} className="customized-content">{t('messagePlatform.btns.apartmentNumber')}</Button>
            </div>)
    }

    function showTenantListPopup() {
        return (<div>
                <ChecklistDropdown options={apartmentDropdownOptions} labelField='label' value={selectedApts} setValueHandler={setSelectedAptsHandler}/>
            </div>
        )
    }

    function setSelectedAptsHandler(value){
        setSelectedApts(value);
    }

    function getMethodsSwitchButtons() {
        return (
            <div className='switch-btns-wrapper'>
                <div className={`switch-btn ${currentMethod === messageMethods.sms ? 'active' : ''}`}
                     onClick={() => setCurrentMethod(messageMethods.sms)}>
                    <img src={sms} alt={messageMethods.sms}/>
                    {t('messagePlatform.btns.sms')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
                <div className={`switch-btn ${currentMethod === messageMethods.email ? 'active' : ''}`}
                     onClick={() => setCurrentMethod(messageMethods.email)} hidden={!isAdmin}>
                    <img src={email} alt={messageMethods.email}/>
                    {t('messagePlatform.btns.email')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
            </div>
        )
    }

    function getContactsSwitchButtons() {
        return (
            <div className='switch-btns-wrapper'>
                <div className={`switch-btn ${currentContactSwitch === contacts.chooseTenants ? 'active' : ''}`}
                     onClick={() => setCurrentContactSwitch(contacts.chooseTenants)}>
                    {t('messagePlatform.btns.chooseTenants')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
                <div className={`switch-btn ${currentContactSwitch === contacts.owners ? 'active' : ''}`}
                     onClick={() => setCurrentContactSwitch(contacts.owners)}>
                    {t('messagePlatform.btns.owners')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
                <div className={`switch-btn ${currentContactSwitch === contacts.renters ? 'active' : ''}`}
                     onClick={() => setCurrentContactSwitch(contacts.renters)}>
                    {t('messagePlatform.btns.renters')}
                    <div className='radio-btn-outer'>
                        <div className='radio-btn-inner'></div>
                    </div>
                </div>
            </div>
        )
    }

    function shouldDisable() {
        if (messagesCount.sms > 50) {
            return true;
        }
        if (currentContactSwitch !== contacts.chooseTenants) {
            return false
        }
        if (selectedApts.length === 0 && currentContactSwitch === contacts.chooseTenants) {
            return true;
        }
    }

    return (
        <StyledMessagePlatformList ltr={ltr}>
            <div className={'select-method-radio'}>
                <label className={'bllink-text-bold'}>{t('messagePlatform.labels.chooseMethod')}</label>
                {getMethodsSwitchButtons()}
            </div>
            <div className={'contacts-wrapper'}>
                <label className={'bllink-text-bold'}>{t('messagePlatform.labels.contact')}</label>
                {getContactsSwitchButtons()}
                <div className={'select-dropdown'} >
                    <div hidden={currentContactSwitch !== contacts.chooseTenants} className={"form-control js-branch bllink-input-border"} id={'select-wrapper'} onClick={handleTenantList}>
                        <label >{t('messagePlatform.labels.selection')}</label>
                        <div id='arrow-btn' className={'bllink-text-bold'} onClick={handleTenantList}>
                            <img src={arrowDownBlack} alt={messageMethods.email}
                                 style = {{
                                     transform: showTenantList ? 'rotate(180deg)' : '',
                                     transition: 'transform 150ms ease',
                                 }}/>
                        </div>
                    </div>
                    {showTenantList && showTenantListPopup()}
                </div>
            </div>
            <div className={'message-content'}>
                <label>{currentMethod && t(`messagePlatform.labels.${currentMethod}MessageContent`)}</label>
                <textarea className="form-control js-branch bllink-input-border"
                          onChange={e => onUpdateMessageInputValues(e)} value={personalizedMessage} rows={10}
                          ref={textareaRef}/>
                <div className={'message-properties'}>
                    <div className={'customize-content'}>
                        <label hidden={true}>יתרת הודעות</label>
                        <label>{`${t('messagePlatform.labels.remainingMessages')} ${Math.max(0, 50 - messagesCount.sms)}`} </label>
                    </div>
                    {isAdmin && (<div className={'customized-content-wrapper'}>
                        <div className={'addBtnContainer'}>
                            <div id='addBtn' className={'bllink-text-bold'} onClick={handleMessageCharactersPopup}>+
                            </div>
                            <label
                                className={'bllink-text-bold'}>{t('messagePlatform.labels.personalizedContent')}</label>
                        </div>
                        {showCustomizedContentPopup && getCustomizedButtons()}
                    </div>)}
                </div>
            </div>
            <div className={'sender-name-input'} hidden={currentMethod === messageMethods.email}>
                <label>{t('messagePlatform.labels.senderName')}</label>
                <textarea className="form-control js-branch bllink-input-border" value={senderName}
                          onChange={e => onUpdateSenderInputValues(e)} rows={1}/>
            </div>
            <div className={'send-button'}>
                <Button onClick={handleSendMessageClick}
                        disabled={shouldDisable()}>{t('messagePlatform.btns.sendMessage')}</Button>
            </div>
        </StyledMessagePlatformList>
    )
}
